import type { GridSortDirection } from '@mui/x-data-grid';
import { ticketPaginationKeys } from 'hooks/queryKeys';
import type { TicketFieldStatus } from '../TicketFields/types';
import type { PlatformType } from './useCreateNewTicket/types';

// Board
export const getBoardViewDetailsKeys = (boardId: string, viewId: string) => [
  'getBoardViewDetailsKeys',
  boardId,
  viewId,
];

export const getDownloadBoardViewKeys = (
  boardId: string,
  viewId: string,
  sortBy?: string | null
) => ['downloadBoardViewKeys', boardId, viewId, sortBy];

// Board Membership
export const getBoardMembershipKeys = (boardId: string) => [
  'getBoardMembershipKeys',
  boardId,
];
export const getBoardMembersKeys = (
  boardId: string,
  limit?: number,
  offset?: number,
  search?: string
) => ['getBoardMembersKeys', boardId, limit, offset, search];

// Permission Group
export const getPermissionGroupAllKeys = ['getPermissionGroupKeys'];

export const getPermissionGroupKeys = (boardId: string) => [
  ...getPermissionGroupAllKeys,
  boardId,
];
export const createPermissionGroupKeys = ['createPermissionGroupKeys'];
export const deletePermissionGroupKeys = ['deletePermissionGroupKeys'];
export const updatePermissionGroupKeys = ['updatePermissionGroupKeys'];

// Ticket Layouts
export const getTicketLayoutAllKeys = ['getTicketLayoutKeys'];
export const restoreTicketLayoutKeys = ['restoreTicketLayoutKeys'];
export const deleteTicketLayoutsKeys = ['deleteTicketLayoutsKeys'];
export const createTicketLayoutsKeys = ['createTicketLayoutsKeys'];
export const updateTicketLayoutsKeys = ['updateTicketLayoutsKeys'];

export const convertTicketLayoutKeys = ['convertTicketLayoutKeys'];

export const getTicketLayoutsKeys = (boardId: string) => [
  ...getTicketLayoutAllKeys,
  boardId,
];

export const getTicketLayoutDetailsKeys = (layoutId: string) => [
  'getTicketLayoutDetailsKeys',
  layoutId,
];

// Ticket Fields
export const getTicketFieldsKeys = ['getTicketFieldsKeys'];
export const createTicketFieldsKeys = ['createTicketFieldsKeys'];
export const restoreTicketFieldKeys = ['restoreTicketFieldKeys'];
export const updateTicketFieldsKeys = ['updateTicketFieldsKeys'];
export const deleteTicketFieldsKeys = (ticketFieldId: string) => [
  'deleteTicketFieldsKeys',
  ticketFieldId,
];
export const getDefaultTicketFieldsKeys = ['getDefaultTicketFieldsKeys'];
export const getTicketFieldKeys = (fieldId: string) => [
  'getTicketField',
  fieldId,
];

// Tickets
export const getTicketDetails = (ticketId: string | number) => [
  'getTicketDetails',
  ticketId,
];
export const getTicketSenderEmailKeys = (ticketId: string) => [
  'getTicketSenderEmail',
  ticketId,
];
export const hideTicketAttachmentKeys = ['hideTicketAttachmentKeys'];
export const getTicketAttachmentsKeys = (
  ticketId: string,
  boardId?: string
) => ['getTicketAttachmentsKeys', ticketId, ...(boardId ? [boardId] : [])];
export const getTicketAttachmentBufferKeys = (ticketAttachmentId: string) => [
  'getTicketAttachmentBufferKeys',
  ticketAttachmentId,
];
export const uploadTicketAttachmentsKeys = (ticketId: string) => [
  'uploadTicketAttachmentsKeys',
  ticketId,
];
export const renameTicketAttachmentsKeys = (ticketAttachmentId: string) => [
  'renameTicketAttachmentsKeys',
  ticketAttachmentId,
];

export const getTicketEmailFileKeys = (ticketId: string, messageId: string) => [
  'getTicketEmailFileKeys',
  ticketId,
  messageId,
];

// STATUS

export const getTicketFieldStatusGroupKeys = () => [
  'getTicketFieldStatusGroupKeys',
];

export const getTicketFieldStatusOptions = (statusOptionId: string) => [
  'getTicketFieldStatusOptions',
  statusOptionId,
];

export const getTicketDetailsPublic = (ticketId: string | number) => [
  'getTicketDetailsPublic',
  ticketId,
];

export const getStatusHistoryKeys = (ticketId: string, fieldId: string) => [
  'getStatusHistory',
  ticketId,
  fieldId,
];

export const executeGetTicketsKeys = ['executeGetTicketsKeys'];
export const previewGetTicketsKeys = ['previewGetTicketsKeys'];

export const getExecuteOrPreviewTicketKeys = (
  boardId: string,
  viewId: string,
  hasUnsavedChanges: boolean
) => [
  ...ticketPaginationKeys(boardId, viewId),
  ...(hasUnsavedChanges ? previewGetTicketsKeys : executeGetTicketsKeys),
];

export const getBoardsKeys = ['getBoardsKeys'];
export const getUserBoardsKeys = ['getUserBoardsKeys'];
export const getOnboardingEligibilityKeys = ['getOnboardingEligibilityKeys'];
export const getBoardViewsKeys = (boardId: string) => [
  'getBoardViewsKeys',
  boardId,
];

export const getBoardTicketsKeys = (boardId: string, search: string) => [
  'getBoardTicketsKeys',
  boardId,
  search,
];

export const mergeTicketKeys = ['mergeTicketKeys'];

export const getTicketPermissionKeys = (ticketId: string) => [
  'getTicketPermissionKeys',
  ticketId,
];

// Ticket Threads
export const getInfiniteTicketThreadsKeys = (ticketId: string) => [
  'getInfiniteTicketThreadsKeys',
  ticketId,
];
export const getTicketThreadsKeys = (ticketId: string) => [
  'getTicketThreadsKeys',
  ticketId,
];
export const renameTicketThreadKeys = (threadId: string) => [
  'renameTicketThreadKeys',
  threadId,
];
export const deleteTicketThreadKeys = ['deleteTicketThreadKeys'];

// Ticket Message
export const getInfiniteTicketMessagesKeys = (
  ticketId: string,
  threadId?: string,
  platformType?: PlatformType
) => ['getInfiniteTicketMessagesKeys', ticketId, threadId, platformType];
// Prefix key for React Query partial matching — invalidates the infinite
// ticket-messages query across every platformType tab variant
// (undefined/APP/SLACK). Derived from the main helper so a rename/shape
// change to the query key can't drift from invalidation call sites.
export const getInfiniteTicketMessagesPrefixKey = (
  ticketId: string,
  threadId?: string
) => getInfiniteTicketMessagesKeys(ticketId, threadId).slice(0, -1);
export const deleteTicketMessage = ['deleteTicketMessageKeys'];

export const getSlackConfigKeys = ['getSlackConfigKeys'];
export const createSlackConfigKeys = ['createSlackConfigKeys'];
export const updateSlackConfigKeys = ['updateSlackConfigKeys'];
export const getUserSlackConnectionKeys = ['getUserSlackConnectionKeys'];
export const getSlackWorkspaceMembersKeys = ['getSlackWorkspaceMembersKeys'];
export const getSlackChannelMembersKeys = (channelId: string) => [
  'getSlackChannelMembersKeys',
  channelId,
];

export const getAccountDomainKeys = (accountId: number) => [
  'getAccountDomainKeys',
  accountId,
];

export const getTicketCollaboratorsKeys = (
  ticketId?: string,
  filters?: { search?: string; types?: string; limit?: number; offset?: number }
) =>
  ticketId
    ? (['ticketCollaborators', ticketId, filters] as const)
    : (['ticketCollaborators', filters] as const);

export const getInfiniteCommentsKeys = (
  ticketId: string,
  commentType?: 'external' | 'internal'
) => ['ticket-comments', ticketId, commentType].filter(Boolean);

// Ironclad
export const getIroncladOAuthRedirectKeys = [
  'getIroncladOAuthRedirectKeys',
] as const;
export const ironcladOAuthEchangeKeys = ['ironcladOAuthEchangeKeys'] as const;
export const getIroncladCredentialsKeys = ['getIroncladCredentials'] as const;
export const upsertIroncladDefaultBoardKeys = [
  'upsertIroncladDefaultBoard',
] as const;
export const getIroncladDefaultBoardKeys = ['getIroncladDefaultBoard'] as const;
export const importIroncladWorkflowsKeys = ['importIroncladWorkflows'] as const;
export const getIroncladWorkflowConfigKeys = [
  'getIroncladWorkflowConfig',
] as const;
export const getIroncladWorkflowConfigDetailKeys = (configId: string) =>
  ['getIroncladWorkflowConfigDetail', configId] as const;
export const getIroncladWorkflowDataKeys = (ticketId: string) =>
  ['getIroncladWorkflowData', ticketId] as const;
export const getIroncladWorkflowDiffKeys = ['getIroncladWorkflowDiff'] as const;
export const updateIroncladWorkflowDiffKeys = [
  'updateIroncladWorkflowDiff',
] as const;
export const updateIroncladWorkflowConfigSelectedBoardKeys = [
  'updateIroncladWorkflowConfigSelectedBoard',
] as const;
export const deleteIroncladCredentialsKeys = [
  'deleteIroncladCredentials',
] as const;

// Conversion
export const convertDocumentKeys = ['convertDocumentKeys'];

// Cycle Times
export const createCycleTimeConfigsKeys = ['createCycleTimeConfigsKeys'];

// Duration format
export const getDurationFormats = ['getDurationFormats'];

// Measure Options
export const getMeasureOptions = ['getMeasureOptions'];

// Currency options
export const getCurrencyOptions = ['getCurrencyOptions'];

// configs

export const BASE_CYCLE_TIME_CONFIGS_KEYS = 'cycleTimeConfigsKeys';
export const getCycleTimeConfigsKeys = (
  status: TicketFieldStatus,
  sort?: {
    sort: string | undefined;
    order: GridSortDirection;
  }
) => [BASE_CYCLE_TIME_CONFIGS_KEYS, status, sort];

export const updateCycleTimeConfigsKeys = [
  BASE_CYCLE_TIME_CONFIGS_KEYS,
  'updateCycleTimeConfigsKeys',
];

export const getCycleTimeStatusFieldKeys = (
  ticketId: string,
  optionIds: string[] | undefined
) => ['getCycleTimeStatusFieldKeys', ticketId, optionIds];

export const restoreCycleTimeConfigsKeys = [
  BASE_CYCLE_TIME_CONFIGS_KEYS,
  'restoreCycleTimeConfigsKeys',
];

export const deleteCycleTimeConfigsKeys = [
  BASE_CYCLE_TIME_CONFIGS_KEYS,
  'deleteCycleTimeConfigsKeys',
];

export const getCycleTimeConfigsCountKeys = [
  BASE_CYCLE_TIME_CONFIGS_KEYS,
  'getCycleTimeConfigsCountKeys',
];

export const getCycleTimeConfigByStatusIdKeys = (statusId: string) => [
  BASE_CYCLE_TIME_CONFIGS_KEYS,
  'getCycleTimeConfigByStatusIdKeys',
  statusId,
];

export const getCycleTimeConfigsByStatusIdKeys = (
  ticketFieldStatusId: string,
  statusIds: string[]
) => [
  BASE_CYCLE_TIME_CONFIGS_KEYS,
  'getCycleTimeConfigsByStatusIdKeys',
  ticketFieldStatusId,
  statusIds,
];

export const getRequesterTicketDetailsKeys = (ticketId: string) => [
  'getRequesterTicketDetailsKeys',
  ticketId,
];

// Audit Portal
export const auditTicketsBaseKey = ['auditTickets'] as const;
export const auditTicketFieldsBaseKey = ['auditTicketFields'] as const;
export const getAuditTicketDetailsKeys = (ticketId: string) => [
  'getAuditTicketDetailsKeys',
  ticketId,
];
// AI Matter Summary
export const getAIMatterSummaryKeys = (ticketId: string) => [
  'getAIMatterSummaryKeys',
  ticketId,
];

export const getEmailLogsKeys = ['getEmailLogsKeys'];
export const getEmailLogsCountKeys = ['getEmailLogsCountKeys'];
export const moveEmailMessageKeys = ['moveEmailMessageKeys'];
export const createTicketFromEmailKeys = ['createTicketFromEmailKeys'];

// Requester Portal View
export const requesterPortalViewKeys = ['requesterPortalView'];
export const updateRequesterPortalViewKeys = ['updateRequesterPortalView'];

// Collaborators
export const getCollaboratorsKeys = (ticketId: string) => [
  'getCollaboratorsKeys',
  ticketId,
];
export const addBulkCollaboratorsKeys = ['addBulkCollaboratorsKeys'];
export const updateCollaboratorsKeys = ['updateCollaboratorsKeys'];
export const removeCollaboratorsKeys = ['removeCollaboratorsKeys'];

// Tasks
export const getTasksByContextKeys = (
  contextType: string,
  contextId: string
) => ['getTasks', contextType, contextId];
export const createTaskKeys = ['createTaskKeys'];
export const updateTaskKeys = ['updateTaskKeys'];
export const toggleTaskKeys = ['toggleTaskKeys'];
export const deleteTaskKeys = ['deleteTaskKeys'];

// Calendar Events
export const getCalendarEventsByContextKeys = (
  contextType: string,
  contextId: string
) => ['getCalendarEvents', contextType, contextId];
export const createCalendarEventKeys = ['createCalendarEventKeys'];
export const updateCalendarEventKeys = ['updateCalendarEventKeys'];
export const deleteCalendarEventKeys = ['deleteCalendarEventKeys'];

// Ticket History
export const getTicketHistoryKeys = (ticketId: string) => [
  'getTicketHistory',
  ticketId,
];

// Calendar
export const getCalendarByContextKeys = (
  contextType: string,
  contextId: string,
  start: string,
  end: string
) => ['getCalendar', contextType, contextId, start, end];

// Message Timeline
export const messageTimelineBaseKey = ['messageTimeline'] as const;

export const getMessageTimelineKeys = (
  ticketId: string,
  platformTypes?: string
) => [...messageTimelineBaseKey, ticketId, platformTypes].filter(Boolean);

export const getMessageRepliesKeys = (ticketId: string, messageId: string) => [
  ...messageTimelineBaseKey,
  'replies',
  ticketId,
  messageId,
];

export const createTimelineMessageKeys = [
  ...messageTimelineBaseKey,
  'create',
] as const;

export const replyToTimelineMessageKeys = [
  ...messageTimelineBaseKey,
  'reply',
] as const;

export const updateTimelineMessageKeys = [
  ...messageTimelineBaseKey,
  'update',
] as const;
